import type { ButtonListAttachment, WSBotMessage } from '@/types/WSBotMessage'
import { CommandBuilder, type IsTypingCommand, type QuickRepliesCommand, type SetInputTypeCommand } from './commandBuilder'
import { createBotMessage, createButtonList, createCarousel, createFileDownload, createImage, createUserMessage, createVideo, type BotMessage, type ButtonList, type Carousel, type FileDownload, type Media, type UserMessage } from './messageBuilder'
import type { Operator } from '@/types'

const hasIsTyping = (data: WSBotMessage) => data?.hasOwnProperty('typing')
const hasText = (data: WSBotMessage) => !!data?.message?.text
const hasQuickReplies = (data: WSBotMessage) => data?.quick_replies && data?.quick_replies?.length !== 0
const hasButton = (data: WSBotMessage) => data?.message?.attachment?.type === 'button'
const hasCards = (data: WSBotMessage) => data?.message?.attachment?.type === 'cards'
const hasImage = (data: WSBotMessage) => data?.message?.attachment?.type === 'image'
const hasFileDownload = (data: WSBotMessage) => data?.message?.attachment?.type === 'filedownload'
const hasVideo = (data: WSBotMessage) => data?.message?.attachment?.type === 'video'

const isUserMessage = (sender: string, jwtToken: string, messageFrom?: string) => {
  return sender === jwtToken && messageFrom !== 'agent'
}

const hasInlineLinks = (text?: string) => {
  if (!text) return
  return text.match(/(https?:\/\/[^\s]+)/g)
}
const createButtonListFromMessage = (links: RegExpMatchArray, data: WSBotMessage, context: MessageContext) => {
  if (links) {

    // prepare a new artifical message with the buttons
    const newmsg = {
      ...data,
      message: {
        attachment: {
          type: 'button' as const,
          payload: {
            buttons: links.map((url) => ({
              type: 'web_url',
              url,
              title: url,
              payload: undefined
            }))
          }
        }
      },
    }

    const btnmsg = createButtonList(newmsg, context)
    return btnmsg
  }
}

type ParseMessageOptions = {
  uuid?: string
  operator: Operator
  jwtToken: string
  expandBotMessageLinks: boolean
  messageFrom?: string
}

export type MessageContext = {
  source: string
  uuid?: string
  operator: Operator
}

type ParseMessageResult =
  | ButtonList
  | BotMessage
  | UserMessage
  | Carousel
  | FileDownload
  | Media
  | IsTypingCommand
  | QuickRepliesCommand
  | SetInputTypeCommand;
export const parseMessage = (data: WSBotMessage, options: ParseMessageOptions): Array<ParseMessageResult> => {
  const result: ParseMessageResult[] = []
  const { uuid, operator, jwtToken, expandBotMessageLinks, messageFrom } = options

  const source = isUserMessage(data.sender, jwtToken, messageFrom) ? 'user' : 'bot'

  // create the message context with the stuff we have to our hands
  // we get other things when parsing the history of the conversation
  // like uuid, isFromClient for example
  const context: MessageContext = {
    source,
    uuid,
    operator
  }

  if (hasIsTyping(data)) {
    result.push(CommandBuilder.isTyping(data))
  }

  if (hasText(data)) {
    if (isUserMessage(data.sender, jwtToken, messageFrom)) {
      result.push(createUserMessage(data, context))
    } else {
      // If the message was generated by AI, we want a aiTextMessage
      result.push(createBotMessage(data, context))

      // here extract https links from the message if active (livechat e.g.)
      // and create buttons from them

      if (expandBotMessageLinks) {
        const links = hasInlineLinks(data?.message?.text)
        if (links) {
          const btnmsg = createButtonListFromMessage(links, data, context)
          if (btnmsg) result.push(btnmsg)
        }
      }
    }
  }

  if (hasQuickReplies(data)) {
    // handle quick replies

    result.push(CommandBuilder.quickReplies(data))
    if (data.inputType) {
      // TODO use the new create function
      result.push(CommandBuilder.setInputType(data))
    }
  }

  // if we got attachments we need to handle them
  if (data?.message?.attachment) {
    if (hasButton(data)) {
      const attachment = <ButtonListAttachment>data.message.attachment
      // I need to build awkwardly a text message from the button message :(
      // this can be much cleaner
      if (attachment.payload.text) {
        result.push(createBotMessage({
          ...data,
          message: { text: attachment.payload.text }
        }, context))
      }

      result.push(createButtonList(data, context))
    }

    if (hasCards(data)) {
      result.push(createCarousel(data, context))
    }

    if (hasImage(data)) {
      result.push(createImage(data, context))
    }

    if (hasFileDownload(data)) {
      result.push(createFileDownload(data, context))
    }

    if (hasVideo(data)) {
      result.push(createVideo(data, context))
    }
  }

  return result
}
